import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import DownloadConfirmPopup from '../../components/DownloadConfirmPopup/DownloadConfirmPopup';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [pictures, setPictures] = useState([]);
  const [vehicleProfiles, setVehicleProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadPopup, setDownloadPopup] = useState({ isOpen: false, group: null });
  const [shareProgress, setShareProgress] = useState({ isSharing: false, current: 0, total: 0, status: '' });
  const [shareQueue, setShareQueue] = useState({ active: false, group: null, currentIndex: 0, savedIndices: [] });
  const [preparedFiles, setPreparedFiles] = useState([]);
  const [isPreparingImages, setIsPreparingImages] = useState(false);
  const [prepareProgress, setPrepareProgress] = useState({ current: 0, total: 0 });
  const preparedFilesRef = useRef([]);
  const prefetchAbortControllerRef = useRef(null);

  // Auto-clear success message after 8 seconds with proper cleanup
  useEffect(() => {
    if (successMessage) {
      const timeoutId = setTimeout(() => {
        setSuccessMessage(null);
      }, 8000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [successMessage]);

  // Cleanup prefetch on unmount
  useEffect(() => {
    return () => {
      if (prefetchAbortControllerRef.current) {
        prefetchAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Helper function to prepare an image File object for sharing or download (memoized)
  const prepareImageFile = useCallback(async (picture, groupTag, index, signal) => {
    let blob = null;

    // Option A: Try backend download endpoint first (which applies server-side EXIF update)
    if (picture.id) {
      try {
        blob = await apiClient.downloadPictureBlob(picture.id, { signal });
      } catch (backendErr) {
        console.warn(`Backend download endpoint failed for picture ${picture.id}, falling back to direct URL:`, backendErr.message);
      }
    }

    if (!blob) {
      const imageUrl = picture.pictureLink || picture.url || (picture.data ? `data:${picture.content_type};base64,${picture.data}` : null);
      if (!imageUrl) {
        throw new Error(`No image URL available for image ${index + 1}`);
      }

      const response = await fetch(imageUrl, {
        mode: 'cors',
        credentials: 'omit',
        signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image ${index + 1}: ${response.status}`);
      }

      blob = await response.blob();
    }

    // Ensure blob is recognized as image
    if (!blob.type || !blob.type.startsWith('image/')) {
      blob = new Blob([blob], { type: 'image/jpeg' });
    }

    // Ensure filename has .jpg extension
    let fileName = picture.name || `vehicle_${groupTag}_${index + 1}_${Date.now()}`;
    if (!fileName.toLowerCase().endsWith('.jpg') && !fileName.toLowerCase().endsWith('.jpeg')) {
      fileName = fileName + '.jpg';
    }

    return new File([blob], fileName, {
      type: 'image/jpeg'
    });
  }, []);

  const fetchPictures = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.getVehicleProfiles();
      // Transform vehicle profiles to flat picture list with profile info
      const allPictures = [];
      const profiles = [];
      
      if (response.vehicle_profiles && Array.isArray(response.vehicle_profiles)) {
        response.vehicle_profiles.forEach((vehicleData) => {
          const profile = vehicleData.profile || {};
          const pictures = vehicleData.pictures || [];
          const reservations = vehicleData.reservations || [];
          const reservationByPicture = new Map();
          reservations.forEach((reservation) => reservation.picture_ids.forEach((id) => reservationByPicture.set(id, reservation)));
          
          // Get vehicle profile ID from profile or top-level vehicleData
          const vehicleProfileId = profile.id || profile.VehicleProfileId || vehicleData.VehicleProfileId || vehicleData.id;
          
          // Store vehicle profile data
          profiles.push({
            id: vehicleProfileId,
            downloaded: profile.Download_status === true,
            tag: profile.id,
            park_number: profile.Most_recent_park_number,
            make: profile.Make,
            model: profile.Model
          });
          
          pictures.forEach((picture) => {
            const reservation = reservationByPicture.get(picture.id);
            allPictures.push({
              // Picture details
              id: picture.id,
              url: picture.compressedPictureLink || picture.pictureLink, // Use compressed for display
              pictureLink: picture.pictureLink, // Keep original for download
              compressedPictureLink: picture.compressedPictureLink,
              name: picture.id || `${profile.id}_picture`,
              size: parseInt(picture.FileSize) || 0,
              content_type: 'image/jpeg',
              
              // Metadata
              device_make: picture.DeviceMake,
              device_model: picture.DeviceModel,
              shooting_time: picture.Shooting_time,
              shooting_time_epoch: picture.Shooting_time_epoch,
              uploader_id: picture.PictureUploaderId,
              
              // Vehicle profile info (use profile.id as plate/tag number)
              tag_number: profile.plate_number || profile.id,
              reservation_id: reservation?.id,
              park_number: reservation ? reservation.park_number : profile.Most_recent_park_number,
              make: profile.Make,
              model: profile.Model,
              vehicle_id: picture.Vehicle_Id,
              vehicle_profile_id: vehicleProfileId,
              created_date: reservation?.shooting_time || picture.Shooting_time,
              
              // Full data for download
              data: null
            });
          });
        });
      }
      
      setPictures(allPictures);
      setVehicleProfiles(profiles);
    } catch (err) {
      console.error('Error fetching pictures:', err);
      setError(err.message || 'Failed to load pictures');
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies

  // Fetch pictures on mount
  useEffect(() => {
    fetchPictures();
  }, [fetchPictures]);

  const handlePictureClick = useCallback((picture) => {
    setSelectedPicture(picture);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setSelectedPicture(null);
  }, []);

  const handleTagClick = useCallback((group) => {
    // Navigate to vehicle profile details page with the vehicle profile ID
    navigate(`/vehicle-details/${encodeURIComponent(group.vehicle_profile_id || group.tag)}`);
  }, [navigate]);

  const handleDownloadBlock = useCallback((group) => {
    // Show confirmation popup
    setDownloadPopup({ isOpen: true, group });
  }, []);

  // Cancel iOS share queue and any ongoing pre-fetch
  const handleCancelShareQueue = useCallback(() => {
    if (prefetchAbortControllerRef.current) {
      prefetchAbortControllerRef.current.abort();
      prefetchAbortControllerRef.current = null;
    }
    setShareQueue({ active: false, group: null, currentIndex: 0, savedIndices: [] });
    setPreparedFiles([]);
    preparedFilesRef.current = [];
    setIsPreparingImages(false);
    setPrepareProgress({ current: 0, total: 0 });
    setShareProgress({ isSharing: false, current: 0, total: 0, status: '' });
    setSuccessMessage('');
  }, []);

  // Start iOS Web Share queue with background prefetching of all images
  const startIosShareQueue = useCallback((group) => {
    if (prefetchAbortControllerRef.current) {
      prefetchAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    prefetchAbortControllerRef.current = abortController;

    const totalImages = group.pictures.length;
    setShareQueue({
      active: true,
      group: group,
      currentIndex: 0,
      savedIndices: []
    });
    setPreparedFiles([]);
    preparedFilesRef.current = [];
    setIsPreparingImages(true);
    setPrepareProgress({ current: 0, total: totalImages });
    setShareProgress({ 
      isSharing: false, 
      current: 0, 
      total: totalImages, 
      status: 'preparing' 
    });
    setSuccessMessage(`📱 Preparing ${totalImages} ${totalImages === 1 ? 'image' : 'images'} for sharing...`);

    // Concurrently prefetch all images in background, storing each file as soon as ready
    (async () => {
      try {
        let completed = 0;
        const totalImages = group.pictures.length;
        const initialFiles = new Array(totalImages).fill(null);
        preparedFilesRef.current = initialFiles;
        setPreparedFiles([...initialFiles]);

        const filePromises = group.pictures.map(async (picture, index) => {
          const file = await prepareImageFile(picture, group.tag, index, abortController.signal);
          if (abortController.signal.aborted) return null;

          completed++;
          preparedFilesRef.current[index] = file;
          setPreparedFiles([...preparedFilesRef.current]);
          setPrepareProgress({ current: completed, total: totalImages });

          // As soon as the first image is ready, allow sharing immediately!
          if (index === 0) {
            setIsPreparingImages(false);
            setShareProgress({ 
              isSharing: false, 
              current: 0, 
              total: totalImages, 
              status: 'waiting' 
            });
            setSuccessMessage(`✅ Image 1 ready! Click "Share Image 1" to save to Photos.`);
          }

          return file;
        });

        await Promise.all(filePromises);
        if (abortController.signal.aborted) return;

        setIsPreparingImages(false);
        setSuccessMessage(`✅ All ${totalImages} ${totalImages === 1 ? 'image' : 'images'} ready to save to Photos.`);
      } catch (err) {
        if (abortController.signal.aborted) return;
        console.error('❌ Error pre-fetching and preparing images:', err);
        setError(`Failed to prepare images: ${err.message}. Try long-pressing the image instead.`);
        setIsPreparingImages(false);
        setShareQueue({ active: false, group: null, currentIndex: 0, savedIndices: [] });
        setPreparedFiles([]);
        preparedFilesRef.current = [];
        setShareProgress({ isSharing: false, current: 0, total: 0, status: 'error' });
      }
    })();
  }, [prepareImageFile]);

  const handleConfirmDownload = async () => {
    const group = downloadPopup.group;
    if (!group) return;

    // Close popup
    setDownloadPopup({ isOpen: false, group: null });

    // First, update download status in backend
    if (!group.vehicle_profile_id) {
      console.error('Vehicle profile ID is missing');
      setError('Cannot download: Vehicle profile ID is missing');
      return;
    }

    try {
      // Update download status in backend first
      const response = await apiClient.updateVehicleProfileDownloadStatus(group.vehicle_profile_id, true);
      
      // Only proceed with download if API call is successful
      if (response && response.profile) {
        // Update local vehicle profiles state with returned profile data
        const updatedProfile = response.profile;
        const profileId = updatedProfile.id || updatedProfile.VehicleProfileId || group.vehicle_profile_id;
        
        setVehicleProfiles(prev => {
          const existingIndex = prev.findIndex(vp => 
            String(vp.id) === String(profileId) || 
            String(vp.id) === String(group.vehicle_profile_id) ||
            vp.tag === group.tag
          );
          
          if (existingIndex >= 0) {
            // Update existing profile
            return prev.map((vp, index) => 
              index === existingIndex
                ? { 
                    ...vp, 
                    downloaded: updatedProfile.Download_status === true,
                    id: profileId,
                    tag: updatedProfile.id || vp.tag,
                    park_number: updatedProfile.Most_recent_park_number || vp.park_number,
                    make: updatedProfile.Make || vp.make,
                    model: updatedProfile.Model || vp.model
                  }
                : vp
            );
          } else {
            // Add new profile if it doesn't exist
            return [...prev, {
              id: profileId,
              downloaded: updatedProfile.Download_status === true,
              tag: updatedProfile.id || group.tag,
              park_number: updatedProfile.Most_recent_park_number || group.park_number,
              make: updatedProfile.Make || group.make,
              model: updatedProfile.Model || group.model
            }];
          }
        });

        // Detect iOS device and browser
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isSafari = /Safari/i.test(navigator.userAgent) && !/Chrome|CriOS|EdgiOS|FxiOS/i.test(navigator.userAgent);
        
        // Check if Web Share API is available and actually works
        // Safari on iOS: Full support for navigator.share with images
        // Chrome on iOS: Limited/no support for sharing files
        const canUseWebShare = navigator.share && navigator.canShare && isIOS && isSafari;
        
        console.log('Browser detection:', { isIOS, isSafari, canUseWebShare, userAgent: navigator.userAgent });
        
        if (canUseWebShare) {
          // iOS Safari Web Share API: start queue and prefetch all images in background
          startIosShareQueue(group);
          return;
        } else {
          // Standard download for desktop or browsers without Web Share API
          const downloadPromises = group.pictures.map(async (picture, index) => {
            try {
              let blob = null;

              // Option A: Try backend download endpoint first (server-side EXIF update)
              if (picture.id) {
                try {
                  blob = await apiClient.downloadPictureBlob(picture.id);
                } catch (backendErr) {
                  console.warn(`Backend download endpoint failed for picture ${picture.id}, falling back to direct URL:`, backendErr.message);
                }
              }

              if (!blob) {
                // Use pictureLink (high quality)
                const imageUrl = picture.pictureLink || picture.url || (picture.data ? `data:${picture.content_type};base64,${picture.data}` : null);
                if (!imageUrl) return;

                console.log(`📥 Downloading image...`);

                // Fetch the image
                const response = await fetch(imageUrl);
                
                if (!response.ok) {
                  console.error('Failed to fetch image:', response.status);
                  return;
                }
                
                blob = await response.blob();
              }

              // Check blob type
              if (!blob.type || !blob.type.startsWith('image/')) {
                console.warn('Fetched blob is not an image:', blob.type);
                blob = new Blob([blob], { type: 'image/jpeg' });
              }

              console.log(`📥 Image ${index + 1} ready`);
              
              // Standard download - works on all platforms
              // Wrap setTimeout in a Promise to properly await the staggered download
              await new Promise((resolve) => {
                setTimeout(() => {
                  const fileName = picture.name || `${group.tag}_${index + 1}_${Date.now()}.jpg`;
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  resolve();
                }, index * 100); // Stagger by 100ms to ensure browser processes each download
              });
            } catch (error) {
              console.error('Error downloading picture:', picture.name, error);
            }
          });

          // Wait for all downloads to complete
          await Promise.all(downloadPromises);
          
          // Show success message with iOS instructions if applicable
          if (isIOS) {
            if (isSafari) {
              setSuccessMessage('Pictures downloaded! To save to Photos: Go to Files app → Downloads → Select images → Tap Share icon → Save Image');
            } else {
              // Chrome, Edge, Firefox on iOS
              setSuccessMessage('Pictures downloaded! Open in Safari for direct Photo Library saving, or: Files app → Downloads → Select images → Share → Save Image');
            }
          } else {
            setSuccessMessage('Pictures downloaded successfully!');
          }
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error updating download status:', error);
      setError(`Unable to start download${error.status ? ` (backend HTTP ${error.status})` : ''}: ${error.message || 'Please try again.'}`);
      // Don't proceed with download if API call fails
    }
  };

  const handleCancelDownload = () => {
    setDownloadPopup({ isOpen: false, group: null });
  };

  // Handle sharing next image in queue (iOS requires user click for each share)
  const handleShareNextImage = async () => {
    if (!shareQueue.active || !shareQueue.group) return;

    const { group, currentIndex, savedIndices } = shareQueue;
    const totalImages = group.pictures.length;
    const file = preparedFilesRef.current[currentIndex] || preparedFiles[currentIndex];

    if (!file) {
      setError(`Image ${currentIndex + 1} is still being prepared. Please wait.`);
      return;
    }

    try {
      setShareProgress({ 
        isSharing: true, 
        current: savedIndices.length + 1, 
        total: totalImages, 
        status: 'sharing' 
      });

      console.log(`📤 Sharing image ${currentIndex + 1}/${totalImages}:`, file.name, file.type);

      // Check if navigator.share supports this file
      if (navigator.canShare && !navigator.canShare({ files: [file] })) {
        throw new Error('Browser cannot share this file type');
      }

      // THIS IS THE KEY: navigator.share() called immediately in user click event!
      // Synchronous access to prepared File preserves the user gesture without timing out!
      await navigator.share({
        files: [file]
      });

      console.log(`✅ Image ${currentIndex + 1} saved!`);

      // Update saved indices
      const newSavedIndices = [...savedIndices, currentIndex];

      // Move to next image or complete
      if (currentIndex + 1 < totalImages) {
        setShareQueue(prev => ({
          ...prev,
          currentIndex: currentIndex + 1,
          savedIndices: newSavedIndices
        }));
        setSuccessMessage(`✅ Image ${currentIndex + 1} saved! Click "Share Image ${currentIndex + 2}" to continue.`);
        setShareProgress({ 
          isSharing: false, 
          current: newSavedIndices.length, 
          total: totalImages, 
          status: 'waiting' 
        });
      } else {
        // All done!
        setShareQueue({ active: false, group: null, currentIndex: 0, savedIndices: [] });
        setPreparedFiles([]);
        preparedFilesRef.current = [];
        setShareProgress({ 
          isSharing: false, 
          current: newSavedIndices.length, 
          total: totalImages, 
          status: 'complete' 
        });
        setSuccessMessage(`🎉 Perfect! All ${totalImages} ${totalImages === 1 ? 'image' : 'images'} saved to your Photo Library!`);
      }
    } catch (error) {
      console.error(`❌ Error sharing image ${currentIndex + 1}:`, error);

      if (error.name === 'AbortError') {
        // User cancelled - still offer to continue or retry
        setSuccessMessage(`Image ${currentIndex + 1} cancelled. Click "Share Image ${currentIndex + 1}" to retry, or Cancel.`);
        setShareProgress({ 
          isSharing: false, 
          current: savedIndices.length, 
          total: totalImages, 
          status: 'waiting' 
        });
      } else {
        setError(`Failed to share image ${currentIndex + 1}: ${error.message}. Try long-pressing the image instead.`);
        setShareQueue({ active: false, group: null, currentIndex: 0, savedIndices: [] });
        setPreparedFiles([]);
        preparedFilesRef.current = [];
        setShareProgress({ isSharing: false, current: 0, total: 0, status: 'error' });
      }
    }
  };

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedPicture) {
        handleCloseLightbox();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedPicture, handleCloseLightbox]);

  // Group pictures by tag_number (memoized)
  const groupPicturesByTag = useMemo(() => (pictures) => {
    const grouped = {};
    
    pictures.forEach((picture) => {
      const tagNumber = picture.tag_number || 'No Tag';
      const groupKey = JSON.stringify([picture.vehicle_profile_id || tagNumber, picture.reservation_id || null]);
      if (!grouped[groupKey]) {
        // Find vehicle profile - prioritize vehicle_profile_id matching
        let vehicleProfile = null;
        if (picture.vehicle_profile_id) {
          vehicleProfile = vehicleProfiles.find(vp => 
            String(vp.id) === String(picture.vehicle_profile_id) ||
            vp.id === picture.vehicle_profile_id
          );
        }
        // Fallback to tag matching if vehicle_profile_id match didn't work
        if (!vehicleProfile) {
          vehicleProfile = vehicleProfiles.find(vp => 
            vp.tag === tagNumber ||
            String(vp.tag) === String(tagNumber)
          );
        }
        
        grouped[groupKey] = {
          tag: tagNumber,
          make: picture.make,
          model: picture.model,
          park_number: picture.park_number,
          created_date: picture.created_date,
          vehicle_profile_id: picture.vehicle_profile_id,
          reservation_id: picture.reservation_id,
          downloaded: vehicleProfile ? (vehicleProfile.downloaded === true) : false,
          pictures: []
        };
      }
      grouped[groupKey].pictures.push(picture);
    });

    return Object.values(grouped);
  }, [vehicleProfiles]); // Depends on vehicleProfiles

  // Split groups into blocks of 4 pictures max (memoized)
  const splitIntoBlocks = useMemo(() => (groups) => {
    const blocks = [];
    const MAX_PICTURES_PER_BLOCK = 4;

    groups.forEach((group) => {
      const totalPictures = group.pictures.length;
      
      if (totalPictures <= MAX_PICTURES_PER_BLOCK) {
        // If 4 or fewer pictures, keep as one block
        blocks.push(group);
      } else {
        // Split into multiple blocks of 4 pictures each
        for (let i = 0; i < totalPictures; i += MAX_PICTURES_PER_BLOCK) {
          const chunkPictures = group.pictures.slice(i, i + MAX_PICTURES_PER_BLOCK);
          blocks.push({
            tag: group.tag,
            make: group.make,
            model: group.model,
            park_number: group.park_number,
            created_date: group.created_date,
            vehicle_profile_id: group.vehicle_profile_id,
            reservation_id: group.reservation_id,
            downloaded: group.downloaded,
            pictures: chunkPictures
          });
        }
      }
    });

    return blocks;
  }, []); // No dependencies - pure function

  // Filter and group pictures (memoized to prevent recalculation)
  const filteredGroups = useMemo(() => {
    const groups = groupPicturesByTag(pictures).filter(group => {
      if (!searchQuery) return true;
      
      const query = searchQuery.toLowerCase();
      
      // Search in both plate number (tag) and park number
      const matchesTag = group.tag && group.tag.toLowerCase().includes(query);
      const matchesParkNumber = group.park_number && group.park_number.toLowerCase().includes(query);
      
      return matchesTag || matchesParkNumber;
    });
    
    // Sort by created_date descending (newest first)
    return groups.sort((a, b) => {
      const dateA = a.created_date ? new Date(a.created_date).getTime() : 0;
      const dateB = b.created_date ? new Date(b.created_date).getTime() : 0;
      return dateB - dateA; // Descending order
    });
  }, [pictures, searchQuery, groupPicturesByTag]);

  const pictureGroups = useMemo(() => {
    return splitIntoBlocks(filteredGroups);
  }, [filteredGroups, splitIntoBlocks]);

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="main-title">Dashboard</div>
        <div className="dashboard-controls">
          <input
            type="text"
            placeholder="Search by plate or park number..."
            className="plate-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="refresh-btn" onClick={fetchPictures} disabled={isLoading}>
            {isLoading ? '↻ Loading...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {/* Share Progress Indicator (iOS) */}
      {shareProgress.isSharing && (
        <div className="share-progress-banner">
          <div className="progress-content">
            <div className="progress-text">
              <strong>
                {shareProgress.status === 'retrying' ? '🔄 Retrying...' : '📱 Sharing...'}
              </strong>
              <span> {shareProgress.current} of {shareProgress.total} images</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${(shareProgress.current / shareProgress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* iOS Share Queue Button */}
      {shareQueue.active && (
        <div className="share-queue-banner">
          <div className="queue-info">
            <strong>{isPreparingImages ? '⏳ Preparing Images...' : '📤 Ready to Share'}</strong>
            <span> Image {shareQueue.currentIndex + 1} of {shareQueue.group.pictures.length}</span>
            {shareQueue.savedIndices.length > 0 && (
              <span className="saved-count"> ({shareQueue.savedIndices.length} already saved)</span>
            )}
            {isPreparingImages && (
              <span className="preparing-count"> ({prepareProgress.current}/{prepareProgress.total} ready)</span>
            )}
          </div>
          <button 
            onClick={handleShareNextImage}
            className="share-next-btn"
            disabled={shareProgress.isSharing || !preparedFiles[shareQueue.currentIndex]}
          >
            {!preparedFiles[shareQueue.currentIndex]
              ? `⏳ Preparing (${prepareProgress.current}/${prepareProgress.total})...`
              : shareProgress.isSharing 
                ? '⏳ Sharing...' 
                : `📤 Share Image ${shareQueue.currentIndex + 1} of ${shareQueue.group.pictures.length}`
            }
          </button>
          <button 
            onClick={handleCancelShareQueue}
            className="cancel-share-btn"
          >
            ✕ Cancel
          </button>
        </div>
      )}

      <div className="dashboard-content">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div>Loading pictures...</div>
          </div>
        ) : pictures.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📷</div>
            <div className="empty-text">No pictures found</div>
            <div className="empty-subtext">Upload pictures to see them here</div>
          </div>
        ) : pictureGroups.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <div className="empty-text">No matching plate numbers</div>
            <div className="empty-subtext">Try a different search query</div>
          </div>
        ) : (
          <div className="picture-blocks-container">
            {pictureGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="picture-block">
                {/* Grey Banner Header */}
                <div className="block-banner">
                  <div className="banner-main">
                    <div className="banner-tag">
                      <span className="tag-icon">🏷️</span>
                      <span 
                        className="tag-text clickable" 
                        onClick={() => handleTagClick(group)}
                        title="View vehicle profile details"
                      >
                        {group.tag}
                      </span>
                    </div>
                    {group.park_number && (
                      <div className="banner-car-info">
                        <span className="car-icon">🅿️</span>
                        <strong>Park: {group.park_number}</strong>
                      </div>
                    )}
                  </div>
                  <div className="banner-meta">
                    {group.reservation_id && <span>Reservation: {group.reservation_id}</span>}
                    {group.created_date && (
                      <span className="banner-date">
                        📅 {new Date(group.created_date).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false
                        })}
                      </span>
                    )}
                    <button 
                      className={`banner-download-btn ${group.downloaded === true ? 'downloaded' : ''}`}
                      onClick={() => handleDownloadBlock(group)}
                      title="Download all pictures in this block"
                    >
                      {group.downloaded === true ? '✓ Downloaded' : '⬇ Download'}
                    </button>
                  </div>
                </div>

                {/* Pictures Grid (max 5 per row) */}
                <div className="block-pictures-grid">
                  {group.pictures.map((picture, index) => {
                    const imageSource = picture.url || (picture.data ? `data:${picture.content_type};base64,${picture.data}` : null);
                    
                    return (
                      <div key={picture.name || index} className="picture-card">
                        <div 
                          className="picture-thumbnail"
                          onClick={() => handlePictureClick(picture)}
                        >
                          <img 
                            src={imageSource} 
                            alt={picture.name || 'Picture'} 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </div>
                        <div className="picture-info">
                          <div className="picture-name">{picture.name || 'Untitled'}</div>
                          <div className="picture-meta">
                            {picture.last_modified && (
                              <div className="picture-date">
                                {new Date(picture.last_modified).toLocaleDateString()}
                              </div>
                            )}
                            {picture.size && (
                              <div className="picture-size">
                                {(picture.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedPicture && (
        <div className="lightbox-overlay" onClick={handleCloseLightbox}>
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={handleCloseLightbox}>
              ✕
            </button>
            
            <div className="lightbox-main">
              {/* Image Display */}
              <div className="lightbox-image-container">
                <img 
                  src={selectedPicture.url || (selectedPicture.data ? `data:${selectedPicture.content_type};base64,${selectedPicture.data}` : null)}
                  alt={selectedPicture.name || 'Picture'} 
                  className="lightbox-image"
                />
              </div>

              {/* Details Panel */}
              <div className="lightbox-details-panel">
                <div className="lightbox-details-header">
                  <h2>Picture Details</h2>
                </div>
                
                <div className="lightbox-details-content">
                  {/* File Name */}
                  <div className="detail-group">
                    <label className="detail-label">File Name</label>
                    <div className="detail-value">{selectedPicture.name || 'Untitled'}</div>
                  </div>

                  {/* File Size */}
                  {selectedPicture.size && (
                    <div className="detail-group">
                      <label className="detail-label">File Size</label>
                      <div className="detail-value">
                        {(selectedPicture.size / 1024 / 1024).toFixed(2)} MB
                        <span className="detail-subtext">
                          ({selectedPicture.size.toLocaleString()} bytes)
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Content Type */}
                  {selectedPicture.content_type && (
                    <div className="detail-group">
                      <label className="detail-label">Type</label>
                      <div className="detail-value">{selectedPicture.content_type}</div>
                    </div>
                  )}

                  {/* Shooting Time */}
                  {selectedPicture.shooting_time && (
                    <div className="detail-group">
                      <label className="detail-label">Shooting Time</label>
                      <div className="detail-value">
                        {new Date(selectedPicture.shooting_time).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                        <span className="detail-subtext">
                          {new Date(selectedPicture.shooting_time).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Device Info */}
                  {(selectedPicture.device_make || selectedPicture.device_model) && (
                    <div className="detail-group">
                      <label className="detail-label">Device</label>
                      <div className="detail-value">
                        {selectedPicture.device_make && <span>{selectedPicture.device_make}</span>}
                        {selectedPicture.device_make && selectedPicture.device_model && ' '}
                        {selectedPicture.device_model && <span>{selectedPicture.device_model}</span>}
                      </div>
                    </div>
                  )}

                  {/* Vehicle Info */}
                  {(selectedPicture.make || selectedPicture.model) && (
                    <div className="detail-group">
                      <label className="detail-label">Vehicle</label>
                      <div className="detail-value">
                        {selectedPicture.make && <strong>{selectedPicture.make}</strong>}
                        {selectedPicture.make && selectedPicture.model && ' '}
                        {selectedPicture.model && <span>{selectedPicture.model}</span>}
                      </div>
                    </div>
                  )}

                  {/* Plate Number */}
                  {selectedPicture.tag_number && (
                    <div className="detail-group">
                      <label className="detail-label">Profile ID (Plate)</label>
                      <div className="detail-value">{selectedPicture.tag_number}</div>
                    </div>
                  )}

                  {/* Park Number */}
                  {selectedPicture.park_number && (
                    <div className="detail-group">
                      <label className="detail-label">Park Number</label>
                      <div className="detail-value">{selectedPicture.park_number}</div>
                    </div>
                  )}

                  {/* Vehicle ID */}
                  {selectedPicture.vehicle_id && (
                    <div className="detail-group">
                      <label className="detail-label">Vehicle ID</label>
                      <div className="detail-value">{selectedPicture.vehicle_id}</div>
                    </div>
                  )}

                  {/* Uploader */}
                  {selectedPicture.uploader_id && (
                    <div className="detail-group">
                      <label className="detail-label">Uploaded By</label>
                      <div className="detail-value">{selectedPicture.uploader_id}</div>
                    </div>
                  )}

                  {/* URL */}
                  {selectedPicture.url && (
                    <div className="detail-group">
                      <label className="detail-label">Storage URL</label>
                      <div className="detail-value detail-url">
                        <a 
                          href={selectedPicture.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="detail-link"
                        >
                          View in Browser ↗
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="detail-actions">
                    {selectedPicture.url && (() => {
                      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                      const canShare = navigator.share && navigator.canShare && isIOS;
                      
                      if (canShare) {
                        // iOS with Web Share API - use button to trigger sharing
                        return (
                          <button 
                            onClick={async () => {
                              try {
                                console.log('📥 Fetching image for sharing...');
                                const response = await fetch(selectedPicture.url, {
                                  mode: 'cors',
                                  credentials: 'omit'
                                });
                                
                                if (!response.ok) {
                                  throw new Error(`Failed to fetch: ${response.status}`);
                                }
                                
                                let blob = await response.blob();
                                console.log('📦 Blob:', blob.type, blob.size);
                                
                                // Ensure blob is recognized as image
                                if (!blob.type || !blob.type.startsWith('image/')) {
                                  blob = new Blob([blob], { type: 'image/jpeg' });
                                }
                                
                                // Ensure filename has .jpg extension
                                let fileName = selectedPicture.name || `vehicle_image_${Date.now()}`;
                                if (!fileName.toLowerCase().endsWith('.jpg') && !fileName.toLowerCase().endsWith('.jpeg')) {
                                  fileName = fileName + '.jpg';
                                }
                                
                                // Don't set lastModified - let the browser use EXIF data
                                const file = new File([blob], fileName, { 
                                  type: 'image/jpeg'
                                });
                                
                                console.log('📤 Sharing:', file.name, file.type);
                                
                                // Share only the file (no title/text)
                                await navigator.share({
                                  files: [file]
                                });
                                
                                console.log('✅ Share successful');
                                setSuccessMessage('Image shared! Tap "Save Image" to add to your Photo Library.');
                                handleCloseLightbox();
                              } catch (error) {
                                if (error.name !== 'AbortError') {
                                  console.error('❌ Error sharing:', error);
                                  setError(`Share failed: ${error.message}. Try long-pressing the image instead.`);
                                }
                              }
                            }}
                            className="detail-action-btn download-btn"
                          >
                            📤 Share to Photos
                          </button>
                        );
                      } else {
                        // Desktop or non-iOS - use standard download
                        return (
                          <a 
                            href={selectedPicture.url} 
                            download={selectedPicture.name}
                            className="detail-action-btn download-btn"
                          >
                            ⬇ Download
                          </a>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Download Confirmation Popup */}
      <DownloadConfirmPopup
        isOpen={downloadPopup.isOpen}
        onClose={handleCancelDownload}
        onConfirm={handleConfirmDownload}
        fileCount={downloadPopup.group?.pictures?.length || 0}
        groupName={downloadPopup.group?.tag || ''}
      />
    </div>
  );
};

export default Dashboard;
